<template>
    <UserLayout
        :page-title="t('investmentStaking')"
        :page-section="t('growth')"
    >
        <div class="inv-shell" :aria-label="t('investmentStakingDashboard')">
            <div class="inv-inner">

                <!-- ── HEADER: Welcome strip + Stat cards ── -->
                <PageHeader :stats="headerStats"/>

                <!-- ── AI RECOMMENDATION NOTICE ── -->
                <Transition
                    enter-active-class="transition-all duration-300 ease-out"
                    enter-from-class="opacity-0 -translate-y-4"
                    enter-to-class="opacity-100 translate-y-0"
                    leave-active-class="transition-all duration-200 ease-in"
                    leave-from-class="opacity-100 translate-y-0"
                    leave-to-class="opacity-0 -translate-y-4"
                >
                    <div v-if="showAiRecommendationNotice" class="inv-ai-notice anim-up" style="--d:200ms">
                        <div class="flex items-start gap-4">
                            <!-- AI icon -->
                            <div class="inv-ai-ic" :style="`background:rgba(${themeColors.primaryRgb},0.15);box-shadow:0 0 14px rgba(${themeColors.primaryRgb},0.2);`">
                                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24" :style="`color:${themeColors.primary}`">
                                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"/>
                                </svg>
                            </div>
                            <div class="flex-1 min-w-0">
                                <div class="flex items-center gap-2 mb-2">
                                    <h3 class="text-sm font-800 text-white">{{ t('aiRecommendation') }}</h3>
                                    <span class="inv-ai-badge" :style="`background:rgba(${themeColors.primaryRgb},0.18);color:${themeColors.primary};`">AI Smart</span>
                                </div>
                                <p class="text-xs text-slate-300 mb-4 leading-relaxed">
                                    {{ t('aiRecommendedPlan') }} <strong class="text-white">{{ preSelectedPlan?.name }}</strong>
                                    <span v-if="suggestedAmount">
                                        {{ t('withSuggestedAmount') }}
                                        <strong :style="`color:${themeColors.primary}`">{{ currencySymbol }}{{ formatCurrency(suggestedAmount) }}</strong>
                                    </span>
                                </p>
                                <div class="flex flex-wrap gap-3">
                                    <button
                                        @click="scrollToRecommendedPlan"
                                        class="inv-btn-primary"
                                        :style="`background:linear-gradient(135deg,${themeColors.primaryDark},${themeColors.primary});box-shadow:0 4px 14px rgba(${themeColors.primaryRgb},0.35);`"
                                    >
                                        {{ t('viewPlan') }}
                                        <svg width="12" height="12" fill="none" stroke="currentColor" stroke-width="2.5" viewBox="0 0 24 24"><path stroke-linecap="round" d="M9 5l7 7-7 7"/></svg>
                                    </button>
                                    <button @click="dismissAiNotice" class="inv-btn-secondary">{{ t('dismiss') }}</button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Transition>

                <!-- ── TYPE TOGGLE ── -->
                <div class="anim-up" style="--d:220ms;display:flex;justify-content:center;">
                    <TypeToggle :active-type="activeType" @update:activeType="activeType = $event"/>
                </div>

                <!-- ── INVESTMENT PLANS SECTION ── -->
                <section v-if="activeType === 'investment'" class="inv-section anim-up" style="--d:260ms">
                    <SectionTitle :title="t('investmentPlans')" :subtitle="t('investmentPlansSubtitle')"/>
                    <InvestmentPlansGrid
                        ref="investmentPlansRef"
                        :plans="investmentPlans"
                        :highlighted-plan-id="preSelectedPlan?.id"
                        @select-plan="selectInvestmentPlan"
                    />
                    <InvestmentLogs
                        :logs="investmentLogs"
                        :show-modal-prop="false"
                        @view-details="viewInvestmentDetails"
                    />
                </section>

                <!-- ── STAKING SECTION ── -->
                <section v-if="activeType === 'staking'" class="inv-section anim-up" style="--d:260ms">
                    <SectionTitle :title="t('stakingPools')" :subtitle="t('stakingPoolsSubtitle')"/>
                    <StakingPoolsGrid :pools="stakingPools" @select-pool="selectStakingPool"/>
                    <StakingLogs
                        :logs="stakingLogs"
                        :btc-price-usd="btcPriceUsd"
                        @unstake="handleUnstake"
                        @claim-rewards="handleClaimRewards"
                        @view-details="viewStakingDetails"
                    />
                </section>

            </div>
        </div>
        <InvestmentModal
            v-if="showInvestmentModal"
            :show="showInvestmentModal"
            :plan="selectedPlan"
            :form="investmentForm"
            :processing="processing"
            :wallet-balance="props.walletBalance"
            :credit-balance="props.creditBalance"
            :invest-split-usdt="props.investSplitUsdt"
            :invest-split-credit="props.investSplitCredit"
            :invest-full-balance-enabled="props.investFullBalanceEnabled"
            @close="closeModal"
        />
        <StakingModal
            v-if="showStakingModal"
            :show="showStakingModal"
            :pool="selectedPool"
            :form="stakingForm"
            :processing="processing"
            @close="closeModal"
        />
    </UserLayout>
</template>

<script setup>
import {ref, reactive, computed, onMounted, onUnmounted, nextTick, watch} from 'vue';
import {router, usePage} from '@inertiajs/vue3';
import UserLayout from "@/Layouts/UserLayout/UserLayout.vue";
import {useToast} from "@/composables/useToast.js";
import {useTranslation} from '@/composables/useTranslation';
import StakingModal from "@/Components/User/Investment/StakingModal.vue";
import InvestmentModal from "@/Components/User/Investment/InvestmentModal.vue";
import StakingPoolsGrid from "@/Components/User/Investment/StakingPoolsGrid.vue";
import SectionTitle from "@/Components/User/Investment/SectionTitle.vue";
import InvestmentPlansGrid from "@/Components/User/Investment/InvestmentPlansGrid.vue";
import TypeToggle from "@/Components/User/Investment/TypeToggle.vue";
import PageHeader from "@/Components/User/Investment/PageHeader.vue";
import InvestmentLogs from "@/Components/User/Investment/InvestmentLogs.vue";
import StakingLogs from "@/Components/User/Investment/StakingLogs.vue";

const props = defineProps({
    investmentPlans: {
        type: Array,
        default: () => [],
        validator: (value) => Array.isArray(value)
    },
    stakingPools: {
        type: Array,
        default: () => [],
        validator: (value) => Array.isArray(value)
    },
    investmentLogs: {
        type: [Array, Object],
        default: () => [],
    },
    stakingLogs: {
        type: [Array, Object],
        default: () => [],
    },
    headerStats: {
        type: Object,
        required: true,
        validator: (value) => value && typeof value === 'object'
    },
    preSelectedPlan: {
        type: Object,
        default: null
    },
    suggestedAmount: {
        type: [Number, String],
        default: null
    },
    walletBalance: {
        type: Number,
        default: 0
    },
    creditBalance: {
        type: Number,
        default: 0
    },
    investSplitUsdt: {
        type: Number,
        default: 50
    },
    investSplitCredit: {
        type: Number,
        default: 50
    },
    investFullBalanceEnabled: {
        type: Boolean,
        default: false
    },
    btcPriceUsd: {
        type: Number,
        default: 0
    }
});

const {showToast} = useToast();
const {t} = useTranslation();
const page = usePage();

const currencySymbol = computed(() => page.props.currencySymbol || '$');
const activeType = ref('investment');
const showInvestmentModal = ref(false);
const showStakingModal = ref(false);
const showInvestmentDetailsModal = ref(false);
const showStakingDetailsModal = ref(false);
const processing = ref(false);
const selectedPlan = ref(null);
const selectedPool = ref(null);
const selectedInvestment = ref(null);
const selectedStake = ref(null);
const showAiRecommendationNotice = ref(false);
const preFilledAmount = ref(null);
const investmentPlansRef = ref(null);

const investmentForm = reactive({
    amount: 0
});

const stakingForm = reactive({
    amount: 0,
    auto_compound: false
});

const primaryColor = computed(() => page.props.primaryColor || '#86efac');

const hexToRgb = (hex) => {
    if (!hex || typeof hex !== 'string') return {r: 134, g: 239, b: 172};
    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
    } : {r: 134, g: 239, b: 172};
};

const rgbToHex = (r, g, b) => {
    return "#" + ((1 << 24) + (r << 16) + (g << 8) + b).toString(16).slice(1);
};

const adjustColor = (hex, amount) => {
    const rgb = hexToRgb(hex);
    const newR = Math.min(255, Math.max(0, rgb.r + amount));
    const newG = Math.min(255, Math.max(0, rgb.g + amount));
    const newB = Math.min(255, Math.max(0, rgb.b + amount));
    return rgbToHex(newR, newG, newB);
};

const themeColors = computed(() => {
    const { r, g, b } = hexToRgb(primaryColor.value);
    return {
        primary:      primaryColor.value,
        primaryDark:  adjustColor(primaryColor.value, -30),
        primaryLight: adjustColor(primaryColor.value, 40),
        primaryRgb:   `${r}, ${g}, ${b}`
    };
});

const selectInvestmentPlan = (plan) => {
    selectedPlan.value = plan;
    if (props.preSelectedPlan && plan.id === props.preSelectedPlan.id && props.suggestedAmount) {
        investmentForm.amount = parseFloat(props.suggestedAmount);
        preFilledAmount.value = parseFloat(props.suggestedAmount);
    } else {
        investmentForm.amount = plan.min_amount;
        preFilledAmount.value = null;
    }

    showInvestmentModal.value = true;
};

const scrollToRecommendedPlan = () => {
    if (activeType.value !== 'investment') {
        activeType.value = 'investment';
    }

    nextTick(() => {
        const planElement = document.getElementById(`investment-plan-${props.preSelectedPlan.id}`);
        if (planElement) {
            planElement.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });

            planElement.classList.add('ring-4');
            planElement.style.setProperty('--tw-ring-color', `rgba(${themeColors.value.primaryRgb}, 0.5)`);

            setTimeout(() => {
                planElement.classList.remove('ring-4');
            }, 2000);
        }
    });
};

const dismissAiNotice = () => {
    showAiRecommendationNotice.value = false;
    router.visit('/user/investments', {
        replace: true,
        preserveState: true,
        preserveScroll: true
    });
};

const formatCurrency = (amount) => {
    const number = parseFloat(amount) || 0;
    return new Intl.NumberFormat('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
    }).format(number);
};

const selectStakingPool = (pool) => {
    selectedPool.value = pool;
    stakingForm.amount = pool.min_stake_amount;
    stakingForm.auto_compound = false;
    showStakingModal.value = true;
};

const closeModal = () => {
    showInvestmentModal.value = false;
    showStakingModal.value = false;
    showInvestmentDetailsModal.value = false;
    showStakingDetailsModal.value = false;
    selectedPlan.value = null;
    selectedPool.value = null;
    preFilledAmount.value = null;

    if (props.preSelectedPlan) {
        router.visit('/user/investments', {
            replace: true,
            preserveState: true,
            preserveScroll: true
        });
    }

    setTimeout(() => {
        selectedInvestment.value = null;
        selectedStake.value = null;
    }, 300);
};

const viewInvestmentDetails = (log) => {
    selectedInvestment.value = log;
    showInvestmentDetailsModal.value = true;
};

const viewStakingDetails = (log) => {
    selectedStake.value = log;
    showStakingDetailsModal.value = true;
};

const handleUnstake = async (log) => {
    showToast(t('unstakingProcessing'), 'info');
};

const handleClaimRewards = async (log) => {
    showToast(t('claimingRewards'), 'info');
};

const handleKeydown = (event) => {
    if (event.key === 'Escape') {
        if (showInvestmentDetailsModal.value || showStakingDetailsModal.value) {
            closeModal();
        } else if (showAiRecommendationNotice.value) {
            dismissAiNotice();
        }
    }
};

// Watch for flash messages
watch(() => page.props.flash, (flash) => {
    if (flash?.success) {
        showToast(flash.success, 'success');
    }
    if (flash?.error) {
        showToast(flash.error, 'error');
    }
    if (flash?.warning) {
        showToast(flash.warning, 'warning');
    }
    if (flash?.info) {
        showToast(flash.info, 'info');
    }
}, { deep: true });

onMounted(() => {
    document.addEventListener('keydown', handleKeydown);
    
    // Check for flash messages on mount
    const flash = page.props.flash;
    if (flash?.success) showToast(flash.success, 'success');
    if (flash?.error) showToast(flash.error, 'error');
    if (flash?.warning) showToast(flash.warning, 'warning');
    if (flash?.info) showToast(flash.info, 'info');
    
    if (props.preSelectedPlan) {
        showAiRecommendationNotice.value = true;
        activeType.value = 'investment';

        setTimeout(() => {
            scrollToRecommendedPlan();
        }, 1000);
    }
});

onUnmounted(() => {
    document.removeEventListener('keydown', handleKeydown)
})
</script>

<style scoped>
/* ════════ PAGE SHELL — matches Dashboard d-wrap / d-inner spacing exactly ════════ */
.inv-shell {
    position: relative;
    z-index: 10;
    width: 100%;
}
.inv-inner {
    max-width: 1280px;
    margin: 0 auto;
    padding: 16px 14px;
    display: flex;
    flex-direction: column;
    gap: 14px;
}
@media (min-width: 640px)  { .inv-inner { padding: 20px 20px; gap: 16px; } }
@media (min-width: 1024px) { .inv-inner { padding: 20px 24px; gap: 18px; } }

/* ════════ SECTION ════════ */
.inv-section {
    display: flex;
    flex-direction: column;
    gap: 0;
}

/* ════════ ENTRANCE ANIMATION ════════ */
.anim-up { animation: invSlideUp 0.45s cubic-bezier(0.22,1,0.36,1) both; animation-delay: var(--d,0ms); }
@keyframes invSlideUp { from { opacity:0; transform:translateY(20px) } to { opacity:1; transform:none } }

/* ════════ AI NOTICE CARD ════════ */
.inv-ai-notice {
    background: rgba(10,4,22,0.97);
    border: 1px solid rgba(167,93,237,0.28);
    border-radius: 14px;
    padding: 16px;
    position: relative;
    overflow: hidden;
    transition: border-color 0.25s;
}
.inv-ai-notice::before {
    content: '';
    position: absolute;
    top: 0; left: 15%; right: 15%; height: 1px;
    background: linear-gradient(90deg, transparent, rgba(167,93,237,0.4), transparent);
}
.inv-ai-ic {
    width: 40px; height: 40px; border-radius: 11px; flex-shrink: 0;
    display: flex; align-items: center; justify-content: center;
}
.inv-ai-badge {
    font-size: 9px; font-weight: 800;
    padding: 3px 8px; border-radius: 999px;
    letter-spacing: 0.06em;
}
.inv-btn-primary {
    display: inline-flex; align-items: center; gap: 6px;
    padding: 8px 16px; border-radius: 9px;
    font-size: 11px; font-weight: 700; color: #fff;
    border: none; cursor: pointer; transition: all 0.22s;
}
.inv-btn-primary:hover { transform: translateY(-2px); filter: brightness(1.1); }
.inv-btn-secondary {
    display: inline-flex; align-items: center;
    padding: 8px 16px; border-radius: 9px;
    font-size: 11px; font-weight: 700; color: #64748b;
    background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.08);
    cursor: pointer; transition: all 0.22s;
}
.inv-btn-secondary:hover { background: rgba(255,255,255,0.08); color: #94a3b8; }

/* ════════ REDUCED MOTION ════════ */
@media (prefers-reduced-motion: reduce) {
    .anim-up { animation: none !important; opacity: 1; transform: none; }
}
</style>
